import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, type ReactElement } from "react";
import type { MarketplaceAccountView } from "@cx/contracts";
import { ContentHeader, useToast } from "@cx/ui";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

/**
 * Volta do OAuth do Mercado Livre (redirect_uri): posta `code` + `state` uma vez só e volta para a configuração.
 * O servidor confere o state (sessão, empresa, validade) e é idempotente para o mesmo state.
 */
export function MlCallbackPage(): ReactElement {
  const { api } = useSession();
  const search = useSearch({ from: "/integracoes/mercado-livre/retorno" });
  const navigate = useNavigate();
  const toast = useToast();
  const sent = useRef(false);
  const finish = useMutation({
    mutationFn: () =>
      api<MarketplaceAccountView>("/marketplaces/mercadolivre/callback", {
        method: "POST",
        body: JSON.stringify({ code: search.code, state: search.state }),
      }),
    onSuccess: (a) => {
      toast("success", `Conta ${a.nickname} conectada`);
      void navigate({ to: "/preferencias/integracoes/mercado-livre", replace: true });
    },
  });
  const ok = !!search.code && !!search.state && !search.error;
  useEffect(() => {
    if (!ok || sent.current) return;
    sent.current = true;
    finish.mutate();
  }, [ok, finish]);
  return (
    <>
      <ContentHeader
        title="Conectando ao Mercado Livre"
        breadcrumb={["Preferências", "Integrações", "Mercado Livre"]}
      />
      <div className="box card p-3">
        {!ok && (
          <p className="mb-2">
            {search.error
              ? "A autorização foi recusada no Mercado Livre."
              : "Retorno do Mercado Livre incompleto."}
          </p>
        )}
        {ok && finish.isPending && <p className="mb-0">Concluindo a conexão…</p>}
        {finish.isError && <div className="alert alert-danger py-2">{errorMessage(finish.error)}</div>}
        {(!ok || finish.isError) && (
          <Link to="/preferencias/integracoes/mercado-livre">Voltar para a integração</Link>
        )}
      </div>
    </>
  );
}
